import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2, CreditCard, TrendingUp, TrendingDown, Store, EyeOff, Wallet, Home, FileText } from "lucide-react";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);
const REG_ICON = { sumup: CreditCard, incassi: TrendingUp, spese: TrendingDown, cassa: Store, preventivi: EyeOff, black: Wallet, fuori_cassa: Home, fatture: FileText };
const REG_TYPES = ["sumup", "incassi", "cassa", "fatture", "spese", "preventivi", "black", "fuori_cassa"];
const REG_LABEL = { sumup: "SumUp", incassi: "Incassi uff.", cassa: "Cassa", fatture: "Fatture", spese: "Spese uff.", preventivi: "Preventivi", black: "Acquisti black", fuori_cassa: "Fuori cassa" };
const METHOD = { contanti: "Contanti", contante: "Contanti", cassa: "Cassa", sumup: "SumUp", banca: "Banca", deposito: "Deposito", nessuno: "—", sospeso: "Sospeso" };

export default function Register() {
  const { tipo } = useParams();
  const navigate = useNavigate();
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/transactions/register/${tipo}`, { params: { from_date: from, to_date: to, q } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tipo, from, to, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const Icon = REG_ICON[tipo] || TrendingUp;
  const isExpense = ["spese", "black", "fuori_cassa"].includes(tipo);

  return (
    <div data-testid="register-page">
      <PageHeader title={data?.label || "Registro movimenti"} subtitle="Registro dettagliato dei movimenti del settore" testid="register-header">
        <Button variant="outline" className="rounded-full" onClick={() => navigate("/contabilita")} data-testid="register-back"><ArrowLeft className="w-4 h-4 mr-2" /> Contabilità</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="register-switcher">
        {REG_TYPES.map((t) => (
          <Button key={t} size="sm" variant={t === tipo ? "default" : "outline"} className="rounded-full" onClick={() => navigate(`/contabilita/registro/${t}`)} data-testid={`register-tab-${t}`}>{REG_LABEL[t]}</Button>
        ))}
      </div>

      <Card className="p-4 rounded-xl mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="register-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="register-to" /></div>
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setFrom(""); setTo(""); }} data-testid="register-all">Tutto</Button>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
            <Input placeholder="Cerca descrizione, categoria, importo…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-9" data-testid="register-search" />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 rounded-xl" data-testid="register-total">
          <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground"><Icon className="w-4 h-4 text-primary" /> Totale</div>
          <p className={`font-dash text-2xl font-semibold mt-1 ${isExpense ? "text-destructive" : "text-[hsl(var(--success))]"}`}>{eur(data?.total)}</p>
        </Card>
        <Card className="p-4 rounded-xl" data-testid="register-count"><div className="text-xs uppercase text-muted-foreground">Movimenti</div><p className="font-dash text-2xl font-semibold mt-1">{data?.count ?? 0}</p></Card>
        <Card className="p-4 rounded-xl" data-testid="register-tax"><div className="text-xs uppercase text-muted-foreground">di cui IVA</div><p className="font-dash text-2xl font-semibold mt-1">{eur(data?.tax)}</p></Card>
        <Card className="p-4 rounded-xl" data-testid="register-avg"><div className="text-xs uppercase text-muted-foreground">Media movimento</div><p className="font-dash text-2xl font-semibold mt-1">{eur(data?.count ? data.total / data.count : 0)}</p></Card>
      </div>

      {data?.by_month?.length > 1 && (
        <Card className="p-4 rounded-xl mb-6" data-testid="register-months">
          <p className="text-xs uppercase text-muted-foreground mb-2">Totali per mese</p>
          <div className="flex flex-wrap gap-2">
            {data.by_month.map((m) => <Badge key={m.month} variant="secondary" className="rounded-full text-sm py-1 px-3">{m.month} · {eur(m.amount)}</Badge>)}
          </div>
        </Card>
      )}

      <Card className="rounded-xl overflow-hidden" data-testid="register-table-card">
        {loading && !data ? (
          <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Descrizione</TableHead><TableHead>Categoria</TableHead><TableHead>Conto</TableHead><TableHead className="text-right">IVA</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.rows || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessun movimento nel periodo.</TableCell></TableRow>}
                {(data?.rows || []).map((r) => (
                  <TableRow key={r.id} data-testid={`register-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap">{r.date}{r.created_at && tipo === "sumup" ? <span className="block text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span> : null}</TableCell>
                    <TableCell className="max-w-[320px] truncate" title={r.description}>{r.description || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full">{r.category || "—"}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{METHOD[r.payment_method] || r.payment_method || "—"}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{r.tax ? eur(r.tax) : "—"}</TableCell>
                    <TableCell className={`text-right font-medium ${isExpense ? "text-destructive" : ""}`}>{eur(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
